import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Video, Trash2, Loader2, ShieldCheck, ShieldAlert, ShieldQuestion, RefreshCw } from "lucide-react";

const MULTIPART_THRESHOLD = 8 * 1024 * 1024; // 8 MB
const PART_SIZE = 16 * 1024 * 1024; // 16 MB parts → up to 2 GB stays well under 10k parts
const SHA_MAX = 256 * 1024 * 1024; // hash in-browser up to 256 MB (matches server verify cap)

// Full-file SHA-256 (hex) via WebCrypto, for files small enough to hold in memory.
async function sha256Hex(file) {
  if (!file || file.size > SHA_MAX || !window.crypto?.subtle) return null;
  try {
    const buf = await file.arrayBuffer();
    const digest = await window.crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch { return null; }
}

function iconFor(mime) {
  if ((mime || "").startsWith("image/")) return ImageIcon;
  if ((mime || "").startsWith("video/")) return Video;
  return FileText;
}

// Normalise an axios error detail (string, or FastAPI/Pydantic array of objects) into a readable string.
function errMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || x?.detail || "").filter(Boolean).join("; ") || fallback;
  return fallback || err?.message || "Something went wrong";
}

// Direct-to-S3 upload via short-lived presigned URLs. No AWS creds ever touch the browser.
export default function S3Uploader({ recordType, recordId, category = "Other", canManage = true, accept = "image/*,application/pdf,video/*", categories = null, serverSide = false, onChange = null }) {
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState(null); // {name, pct}
  const [selCat, setSelCat] = useState(categories && categories.length ? categories[0] : category);
  const inputRef = useRef();
  const pollRef = useRef({ tries: 0 });

  const load = () => api.get("/attachments", { params: { record_type: recordType, record_id: recordId } })
    .then((r) => { setItems(r.data.items || []); onChange?.(); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [recordType, recordId]);

  // Auto-advance malware scans: while any attachment is still SCANNING, poll the
  // scan-refresh endpoint and reload so the UI resolves to Clean/Blocked on its own.
  useEffect(() => {
    const pending = items.filter((a) => !a.scan_status || a.scan_status === "SCANNING");
    if (pending.length === 0) { pollRef.current.tries = 0; return; }
    if (pollRef.current.tries >= 20) return; // ~80s safety cap
    const t = setTimeout(async () => {
      pollRef.current.tries += 1;
      await Promise.all(pending.map((a) => api.post(`/attachments/${a.id}/scan/refresh`).catch(() => {})));
      load();
    }, 4000);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [items]);

  const putWithProgress = (url, body, contentType, onPct, extraHeaders) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    if (contentType) xhr.setRequestHeader("Content-Type", contentType);
    if (extraHeaders) Object.entries(extraHeaders).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onPct) onPct(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve(xhr) : reject(new Error("S3 " + xhr.status));
    xhr.onerror = () => reject(new Error("network"));
    xhr.send(body);
  });

  const simpleUpload = async (file) => {
    const ct = file.type || "application/octet-stream";
    if (serverSide) {
      const fd = new FormData();
      fd.append("record_type", recordType);
      fd.append("record_id", recordId);
      fd.append("category", selCat);
      fd.append("file", file);
      await api.post("/attachments/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress({ name: file.name, pct: e.total ? Math.round((e.loaded / e.total) * 100) : 0 }),
      });
      return;
    }
    const sha256 = await sha256Hex(file);
    const { data } = await api.post("/attachments/presign", { record_type: recordType, record_id: recordId, category: selCat, filename: file.name, content_type: ct, size: file.size, sha256 });
    // Bucket enforces SSE — the presigned PUT signs ServerSideEncryption=AES256, so the
    // PUT MUST carry the matching header or S3 returns SignatureDoesNotMatch.
    await putWithProgress(data.upload_url, file, ct, (pct) => setProgress({ name: file.name, pct }), { "x-amz-server-side-encryption": "AES256" });
    await api.post(`/attachments/${data.attachment_id}/complete`);
  };

  const multipartUpload = async (file) => {
    const ct = file.type || "application/octet-stream";
    const sha256 = await sha256Hex(file);
    const { data } = await api.post("/attachments-mp/create", { record_type: recordType, record_id: recordId, category: selCat, filename: file.name, content_type: ct, size: file.size, sha256 });
    const total = Math.ceil(file.size / PART_SIZE);
    const parts = [];
    try {
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * PART_SIZE, Math.min(file.size, (i + 1) * PART_SIZE));
        const pu = await api.post("/attachments-mp/part-url", { attachment_id: data.attachment_id, part_number: i + 1 });
        let etag = null, attempt = 0;
        while (attempt < 3 && !etag) {
          try {
            const xhr = await putWithProgress(pu.data.url, blob, null, (pct) => setProgress({ name: file.name, pct: Math.round(((i + pct / 100) / total) * 100) }));
            etag = xhr.getResponseHeader("ETag");
          } catch (e) { attempt++; if (attempt >= 3) throw e; }
        }
        if (!etag) throw new Error("Missing ETag (check S3 CORS ExposeHeaders)");
        parts.push({ part_number: i + 1, etag });
      }
      await api.post("/attachments-mp/complete", { attachment_id: data.attachment_id, parts });
    } catch (e) {
      await api.post("/attachments-mp/abort", { attachment_id: data.attachment_id }).catch(() => {});
      throw e;
    }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProgress({ name: file.name, pct: 0 });
    try {
      if ((file.type || "").startsWith("video/") && file.size > MULTIPART_THRESHOLD) await multipartUpload(file);
      else await simpleUpload(file);
      toast.success("Uploaded — scanning for threats…");
      pollRef.current.tries = 0;
      load();
    } catch (err) {
      toast.error(errMsg(err, `Upload failed: ${err.message}`));
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const view = async (a) => {
    if (a.scan_status && a.scan_status !== "NO_THREATS_FOUND") {
      toast.error(`Not available yet — malware scan: ${a.scan_status}`);
      return;
    }
    try { const r = await api.get(`/attachments/${a.id}/view`); window.open(r.data.url, "_blank"); }
    catch (e) { toast.error(errMsg(e, "Could not open file")); }
  };
  const del = async (a) => {
    try { await api.delete(`/attachments/${a.id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove"); }
  };
  const refreshScan = async (a) => {
    try { await api.post(`/attachments/${a.id}/scan/refresh`); load(); }
    catch { /* ignore */ }
  };

  const ScanBadge = ({ a }) => {
    const s = a.scan_status;
    if (!s || s === "SCANNING") return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600" data-testid={`scan-${a.id}`}>
        <ShieldQuestion className="w-3 h-3" /> Scanning…
        <button onClick={() => refreshScan(a)} className="text-slate-400 hover:text-slate-600" title="Refresh scan" data-testid={`scan-refresh-${a.id}`}><RefreshCw className="w-3 h-3" /></button>
      </span>
    );
    if (s === "NO_THREATS_FOUND") return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600" data-testid={`scan-${a.id}`}><ShieldCheck className="w-3 h-3" /> Clean</span>;
    return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-600" data-testid={`scan-${a.id}`}><ShieldAlert className="w-3 h-3" /> {s === "THREATS_FOUND" ? "Blocked — threat" : `Blocked — ${s}`}</span>;
  };

  return (
    <div data-testid="s3-uploader">
      {items.length === 0 ? <p className="text-sm text-slate-400 mb-3">No files uploaded.</p> : (
        <div className="space-y-1.5 mb-3">
          {items.map((a) => {
            const Ic = iconFor(a.mime);
            const clean = a.scan_status === "NO_THREATS_FOUND";
            return (
              <div key={a.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2" data-testid={`attachment-${a.id}`}>
                <Ic className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <button onClick={() => view(a)} disabled={!clean} className={`text-sm truncate block max-w-full text-left ${clean ? "text-indigo-600 hover:underline" : "text-slate-400 cursor-not-allowed"}`} data-testid={`attachment-view-${a.id}`}>{a.filename}</button>
                  <div className="text-[11px] text-slate-400 flex items-center gap-2">{a.category} · {((a.size || 0) / 1024).toFixed(0)} KB <ScanBadge a={a} /></div>
                </div>
                {canManage && <button onClick={() => del(a)} className="text-slate-300 hover:text-rose-500 shrink-0" data-testid={`attachment-del-${a.id}`}><Trash2 className="w-4 h-4" /></button>}
              </div>
            );
          })}
        </div>
      )}
      {progress && (
        <div className="mb-3" data-testid="s3-upload-progress">
          <div className="flex justify-between text-[11px] text-slate-500 mb-1"><span className="truncate">{progress.name}</span><span>{progress.pct}%</span></div>
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-indigo-500 transition-all" style={{ width: `${progress.pct}%` }} /></div>
        </div>
      )}
      {canManage && (
        <div className="flex flex-wrap items-center gap-2">
          {categories && categories.length > 0 && (
            <select value={selCat} onChange={(e) => setSelCat(e.target.value)} disabled={!!progress} className="rounded-lg border border-slate-200 px-2.5 py-2 text-sm text-slate-600" data-testid="s3-upload-category">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <label className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 cursor-pointer hover:border-indigo-300 ${progress ? "opacity-60 pointer-events-none" : ""}`} data-testid="s3-upload-btn">
            {progress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload file
            <input ref={inputRef} type="file" accept={accept} hidden onChange={onPick} />
          </label>
        </div>
      )}
    </div>
  );
}
